import { useQuery } from '@tanstack/react-query';
import { useParams, Link } from '@tanstack/react-router';
import { getWeather, getAlertsForCounty } from '@/lib/api';
import { queryKeys } from '@/lib/query';
import { LoadingState, SEO, PlaceSchema, BreadcrumbSchema, ForecastPeriodCards, SevenDayForecast, HourlyForecastSection } from '@/components';
import { FaTint, FaWind, FaEye, FaThermometerHalf, FaStar, FaRegStar, FaExternalLinkAlt } from 'react-icons/fa';
import { useFavorites } from '@/hooks';
import { calcDewPoint } from '@/lib/weather';

// Hero images for locations (Unsplash free images)
const heroImages: Record<string, string> = {
  // Major cities
  'detroit': 'https://images.unsplash.com/photo-1534430480872-3498386e7856?w=600&h=300&fit=crop', // Detroit skyline
  'ann-arbor': 'https://images.unsplash.com/photo-1587613863965-74d82b39ef79?w=600&h=300&fit=crop', // Michigan Stadium
  'grand-rapids': 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=600&h=300&fit=crop', // Grand Rapids
  'lansing': 'https://images.unsplash.com/photo-1555881400-74d7acaacd8b?w=600&h=300&fit=crop', // Capitol building
  'kalamazoo': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=600&h=300&fit=crop', // Sunset cityscape
  'flint': 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&h=300&fit=crop', // Urban skyline

  // Lake Michigan / West Michigan
  'holland': 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=600&h=300&fit=crop', // Dutch windmill
  'grand-haven': 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=600&h=300&fit=crop', // Lighthouse pier
  'muskegon': 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=300&fit=crop', // Lake sunset
  'st-joseph': 'https://images.unsplash.com/photo-1501594907352-04cda38ebc29?w=600&h=300&fit=crop', // Lighthouse pier

  // Northern Michigan
  'traverse-city': 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=600&h=300&fit=crop', // Bay/mountains
  'petoskey': 'https://images.unsplash.com/photo-1494500764479-0c8f2919a3d8?w=600&h=300&fit=crop', // Lake view
  'mackinac': 'https://images.unsplash.com/photo-1545558014-8692077e9b5c?w=600&h=300&fit=crop', // Bridge aerial

  // Upper Peninsula
  'marquette': 'https://images.unsplash.com/photo-1478827536114-da961b7f86d2?w=600&h=300&fit=crop', // Lake Superior
  'sault-ste-marie': 'https://images.unsplash.com/photo-1501785888041-af3ef285b470?w=600&h=300&fit=crop', // Water landscape
  'houghton': 'https://images.unsplash.com/photo-1418065460487-3e41a6c84dc5?w=600&h=300&fit=crop', // Winter forest
  'iron-mountain': 'https://images.unsplash.com/photo-1478827536114-da961b7f86d2?w=600&h=300&fit=crop', // Northern landscape

  // Mid-Michigan
  'saginaw': 'https://images.unsplash.com/photo-1477959858617-67f85cf4f1df?w=600&h=300&fit=crop', // City skyline
  'midland': 'https://images.unsplash.com/photo-1508739773434-c26b3d09e071?w=600&h=300&fit=crop', // Sunset scene
  'bay-city': 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=600&h=300&fit=crop', // Bay sunset
};
// Default: Michigan winter/nature scene
const defaultHero = 'https://images.unsplash.com/photo-1491002052546-bf38f186af56?w=600&h=300&fit=crop';

export function WeatherPage() {
  const { locationId } = useParams({ from: '/weather/$locationId' });
  const { isFavorite, toggleFavorite } = useFavorites();
  const favorited = isFavorite(locationId);

  const { data, isLoading, error } = useQuery({
    queryKey: queryKeys.weather.byLocation(locationId),
    queryFn: () => getWeather(locationId),
  });

  // Alerts query - can be used for alert banners
  useQuery({
    queryKey: queryKeys.alerts.byCounty(locationId),
    queryFn: () => getAlertsForCounty(locationId),
  });

  if (isLoading) {
    return <LoadingState message="Loading weather..." />;
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[40vh] text-center px-4">
        <div className="text-6xl mb-4">🌧️</div>
        <h2 className="text-2xl font-bold mb-2">Weather Unavailable</h2>
        <p className="text-white/60 mb-6">
          We couldn't find weather data for "{locationId}". It may not be a valid Michigan location.
        </p>
        <div className="flex gap-4">
          <Link
            to="/"
            className="flex items-center gap-2 px-4 py-2 bg-[var(--color-info)] hover:bg-[var(--color-info)]/80 rounded-lg font-medium transition-colors"
          >
            ← Back to Home
          </Link>
          <Link
            to="/weather/$locationId"
            params={{ locationId: 'detroit' }}
            className="px-4 py-2 stat-box hover:bg-white/10 rounded-lg font-medium transition-colors"
          >
            Try Detroit
          </Link>
        </div>
      </div>
    );
  }

  const { location, current, hourly, daily, timezone } = data;
  const heroImage = heroImages[locationId] || defaultHero;
  const dewPoint = calcDewPoint(current);

  const pageTitle = `${location.name}, MI - ${current.temperature ?? '--'}°F ${current.conditions}`;
  const pageDescription = `Current weather in ${location.name}, Michigan: ${current.conditions}, ${current.temperature ?? '--'}°F. Get hourly and 7-day forecasts, weather alerts, and detailed conditions.`;

  const breadcrumbs = [
    { name: 'Home', url: 'https://miweather.org/' },
    { name: location.name, url: `https://miweather.org/weather/${locationId}` }
  ];

  return (
    <div className="space-y-4">
      <SEO
        title={pageTitle}
        description={pageDescription}
        path={`/weather/${locationId}`}
      />
      <PlaceSchema location={location} current={current} />
      <BreadcrumbSchema items={breadcrumbs} />
      {/* Main Layout: Weather Card + 7-Day Forecast side by side */}
      <div className="flex flex-col md:flex-row md:items-start gap-4">

        {/* Main Weather Card - fixed height to match collapsed 7-day forecast */}
        <div className="flex-1 relative rounded-2xl overflow-hidden h-[280px] md:h-[388px]">
          {/* Background Image */}
          <img
            src={heroImage}
            alt={`${location.name}, Michigan skyline - weather forecast background`}
            className="absolute inset-0 w-full h-full object-cover"
          />
          {/* Dark overlay for readability */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#0d1a2d]/80 via-[#0d1a2d]/40 to-transparent" />

          {/* Content */}
          <div className="relative h-full p-5 flex flex-col">
            {/* Location Name */}
            <div className="flex items-center gap-3 mb-auto">
              <h1 className="text-xl font-bold uppercase tracking-wider">
                {location.name}, MI
              </h1>
              <button
                onClick={() => toggleFavorite(locationId)}
                className="p-1.5 rounded-full stat-box hover:bg-white/20 transition-all hover:scale-110"
                aria-label={favorited ? 'Remove from favorites' : 'Add to favorites'}
                aria-pressed={favorited}
              >
                {favorited ? (
                  <FaStar className="text-yellow-400 text-lg" aria-hidden="true" />
                ) : (
                  <FaRegStar className="text-white/60 text-lg" aria-hidden="true" />
                )}
              </button>
              {location.website && (
                <a
                  href={location.website}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-1.5 rounded-full stat-box hover:bg-white/20 transition-all hover:scale-110"
                  aria-label={`Visit ${location.name} county website (opens in new tab)`}
                >
                  <FaExternalLinkAlt className="text-white/60 text-sm" aria-hidden="true" />
                </a>
              )}
            </div>

            {/* Bottom Content */}
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              {/* Temperature Box */}
              <div className="stat-box rounded-xl p-4">
                <div className="text-5xl font-black leading-none mb-1">{current.temperature ?? '--'}°F</div>
                <p className="text-sm text-white/90">{current.conditions}</p>
                <p className="text-[#4ba3c3] text-sm">Feels like {current.feelsLike ?? '--'}°</p>
              </div>

              {/* Stats Grid - 2x2 */}
              <div className="grid grid-cols-2 gap-2 sm:ml-auto">
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Wind</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaWind className="text-xs text-white/60" /> {current.windSpeed ?? '--'} mph {current.windDirection}
                  </p>
                </div>
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Humidity</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaTint className="text-xs text-white/60" /> {current.humidity}%
                  </p>
                </div>
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Visibility</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaEye className="text-xs text-white/60" /> {current.visibility} mi
                  </p>
                </div>
                <div className="stat-box px-3 py-2 rounded-lg">
                  <p className="text-[10px] text-white/60">Dew Point</p>
                  <p className="text-sm font-bold flex items-center gap-1">
                    <FaThermometerHalf className="text-xs text-white/60" /> {dewPoint ?? '--'}°
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 7-Day Forecast - Right Column */}
        <SevenDayForecast daily={daily} timezone={timezone} />
      </div>

      {/* Forecast Period Cards */}
      <ForecastPeriodCards daily={daily} timezone={timezone} />

      {/* Hourly Forecast - Full Width Below */}
      <HourlyForecastSection hourly={hourly} locationName={location.name} timezone={timezone} />
    </div>
  );
}
